import { useState, useEffect } from 'react';
import { Sparkles, TrendingUp, Users, Mail, Target, BarChart3, Play, Pause, Bell, Map, RefreshCw, UserPlus, Check, X, Trash2, Send } from 'lucide-react';
import apiClient from '../utils/axios';
import AICampaignGenerator from '../components/AICampaignGenerator';
import AttributionVisualizer from '../components/AttributionVisualizer';
import { ConfirmationModal, AlertModal } from '../components/Modal';
import { useModal } from '../hooks/useModal';

export default function NurtureDashboard() {
  const [sequences, setSequences] = useState([]);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [sequenceDetails, setSequenceDetails] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerator, setShowGenerator] = useState(false);
  const [activeTab, setActiveTab] = useState('sequences');
  const [marketEvents, setMarketEvents] = useState([]);
  const [selectedLeadForAttribution, setSelectedLeadForAttribution] = useState(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [updatingStep, setUpdatingStep] = useState(null);
  const [deletingStep, setDeletingStep] = useState(null);
  const [syncingToInstantly, setSyncingToInstantly] = useState(false);
  const { confirmationModal, alertModal, showConfirmation, showAlert } = useModal();

  useEffect(() => {
    fetchSequences();
    fetchMarketEvents();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedSequence) {
      fetchSequenceDetails();
      fetchPredictions();
    }
  }, [selectedSequence]);

  const fetchSequences = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/nurture-sequences');
      setSequences(response.data.sequences || []);
    } catch (error) {
      console.error('Error fetching sequences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSequenceDetails = async () => {
    try {
      const response = await apiClient.get(`/nurture-sequences/${selectedSequence}`);
      setSequenceDetails(response.data.sequence);
    } catch (error) {
      console.error('Error fetching sequence details:', error);
    }
  };

  const fetchPredictions = async () => {
    try {
      const response = await apiClient.get(`/nurture-sequences/${selectedSequence}/predictions`);
      setPredictions(response.data.predictions || []);
    } catch (error) {
      console.error('Error fetching predictions:', error);
    }
  };

  const handleActivate = async (sequenceId) => {
    try {
      await apiClient.put(`/nurture-sequences/${sequenceId}/activate`);
      fetchSequences();
      await showAlert({ title: 'Success', message: 'Sequence activated!', type: 'success' });
    } catch (error) {
      console.error('Error activating sequence:', error);
      await showAlert({ title: 'Error', message: error.response?.data?.message || 'Error activating sequence', type: 'error' });
    }
  };

  const handlePause = async (sequenceId) => {
    try {
      await apiClient.put(`/nurture-sequences/${sequenceId}/pause`);
      fetchSequences();
      await showAlert({ title: 'Success', message: 'Sequence paused!', type: 'success' });
    } catch (error) {
      console.error('Error pausing sequence:', error);
      await showAlert({ title: 'Error', message: error.response?.data?.message || 'Error pausing sequence', type: 'error' });
    }
  };

  const handleSequenceGenerated = (sequence) => {
    setShowGenerator(false);
    fetchSequences();
    if (sequence && sequence.id) {
      setSelectedSequence(sequence.id);
    }
  };

  const fetchMarketEvents = async () => {
    try {
      const response = await apiClient.get('/market-events/recent?hours=168&limit=50');
      setMarketEvents(response.data.events || []);
    } catch (error) {
      console.error('Error fetching market events:', error);
    }
  };

  const handleTriggerSequence = async (eventId) => {
    const confirmed = await showConfirmation({
      title: 'Trigger Sequence',
      message: 'Trigger nurture sequence for this event? This will enroll matching leads.',
      type: 'info'
    });
    
    if (!confirmed) return;

    try {
      await apiClient.post(`/market-events/${eventId}/trigger-sequence`);
      await showAlert({ title: 'Success', message: 'Sequence triggered successfully!', type: 'success' });
      fetchMarketEvents();
    } catch (error) {
      console.error('Error triggering sequence:', error);
      await showAlert({ title: 'Error', message: error.response?.data?.message || 'Error triggering sequence', type: 'error' });
    }
  };

  const fetchAvailableLeads = async () => {
    if (!selectedSequence) return;
    
    try {
      setLoadingLeads(true);
      // Get leads that are ready but not enrolled in this sequence
      const response = await apiClient.get('/leads?status=new&limit=100');
      const allLeads = response.data.leads || [];
      
      // Get already enrolled leads for this sequence
      const enrollmentsResponse = await apiClient.get(`/nurture-sequences/enrollments?sequence_id=${selectedSequence}`);
      const enrolledLeadIds = new Set(
        (enrollmentsResponse.data.enrollments || []).map(e => e.lead_id)
      );
      
      // Filter to only ready leads not already enrolled
      const readyLeads = allLeads.filter(lead => 
        lead.email &&
        lead.first_name &&
        lead.company_name &&
        lead.landing_page_url &&
        lead.status !== 'unsubscribed' &&
        lead.status !== 'suppressed' &&
        !enrolledLeadIds.has(lead.id)
      );
      
      setAvailableLeads(readyLeads);
    } catch (error) {
      console.error('Error fetching available leads:', error);
      await showAlert({ title: 'Error', message: 'Error loading leads: ' + (error.response?.data?.message || error.message), type: 'error' });
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleOpenEnrollModal = () => {
    setShowEnrollModal(true);
    setSelectedLeadIds([]);
    fetchAvailableLeads();
  };

  const handleToggleLeadSelection = (leadId) => {
    setSelectedLeadIds(prev => 
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeadIds.length === availableLeads.length) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(availableLeads.map(lead => lead.id));
    }
  };

  const handleEnrollLeads = async () => {
    if (!selectedSequence || selectedLeadIds.length === 0) {
      await showAlert({ title: 'Warning', message: 'Please select at least one lead to enroll', type: 'warning' });
      return;
    }

    // Check if sequence is active
    if (sequenceDetails && sequenceDetails.status !== 'active') {
      const activate = await showConfirmation({
        title: 'Sequence Not Active',
        message: 'This sequence is not active. Would you like to activate it and enroll the leads?',
        type: 'warning'
      });
      
      if (activate) {
        try {
          await apiClient.put(`/nurture-sequences/${selectedSequence}/activate`);
          // Refresh sequence details after activation
          await fetchSequenceDetails();
        } catch (error) {
          await showAlert({ title: 'Error', message: 'Error activating sequence: ' + (error.response?.data?.message || error.message), type: 'error' });
          return;
        }
      } else {
        return;
      }
    }

    const confirmed = await showConfirmation({
      title: 'Enroll Leads',
      message: `Enroll ${selectedLeadIds.length} lead(s) in this sequence?`,
      type: 'info'
    });
    
    if (!confirmed) return;

    try {
      setEnrolling(true);
      const response = await apiClient.post(
        `/nurture-sequences/${selectedSequence}/enroll`,
        { lead_ids: selectedLeadIds }
      );

      await showAlert({ title: 'Success', message: `Successfully enrolled ${response.data.enrolled_count} lead(s)!`, type: 'success' });
      setShowEnrollModal(false);
      setSelectedLeadIds([]);
      fetchSequences();
      fetchSequenceDetails();
      fetchAvailableLeads();
    } catch (error) {
      console.error('Error enrolling leads:', error);
      const errorMessage = error.response?.data?.message || error.message;
      if (errorMessage.includes('must be active')) {
        await showAlert({ title: 'Warning', message: 'Sequence must be active to enroll leads. Please activate it first.', type: 'warning' });
      } else {
        await showAlert({ title: 'Error', message: 'Error enrolling leads: ' + errorMessage, type: 'error' });
      }
    } finally {
      setEnrolling(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      // Get all templates with content (not just approved, so user has options)
      const response = await apiClient.get('/templates?limit=50');
      const allTemplates = response.data.templates || [];
      // Filter to only templates with HTML content
      const templatesWithContent = allTemplates.filter(t => 
        t.html_content && t.html_content.trim().length > 0
      );
      setTemplates(templatesWithContent);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleAssignTemplate = async (stepId, templateId) => {
    if (!selectedSequence || !stepId) {
      await showAlert({ title: 'Warning', message: 'Please select a sequence and step', type: 'warning' });
      return;
    }

    const confirmed = await showConfirmation({
      title: 'Assign Template',
      message: 'Assign this template to the step? This will disable AI generation.',
      type: 'info'
    });
    
    if (!confirmed) return;

    try {
      setUpdatingStep(stepId);
      
      // First, create a variation from the template if needed
      // For now, we'll use the API endpoint to update the step
      // The backend will handle creating the variation if needed
      
      await apiClient.put(
        `/nurture-sequences/${selectedSequence}/steps/${stepId}`,
        {
          template_id: templateId, // Send template_id, backend will create variation
          ai_generate_content: false
        }
      );

      await showAlert({ title: 'Success', message: 'Template assigned successfully!', type: 'success' });
      fetchSequenceDetails(); // Refresh to show updated step
    } catch (error) {
      console.error('Error assigning template:', error);
      await showAlert({ title: 'Error', message: 'Error assigning template: ' + (error.response?.data?.message || error.message), type: 'error' });
    } finally {
      setUpdatingStep(null);
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!selectedSequence || !stepId) {
      await showAlert({ title: 'Warning', message: 'Please select a sequence and step', type: 'warning' });
      return;
    }

    // Check if sequence is active
    if (sequenceDetails && sequenceDetails.status === 'active') {
      await showAlert({ title: 'Warning', message: 'Cannot delete steps from an active sequence. Please pause the sequence first.', type: 'warning' });
      return;
    }

    const confirmed = await showConfirmation({
      title: 'Delete Step',
      message: 'Are you sure you want to delete this step? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;

    try {
      setDeletingStep(stepId);
      
      await apiClient.delete(
        `/nurture-sequences/${selectedSequence}/steps/${stepId}`
      );

      await showAlert({ title: 'Success', message: 'Step deleted successfully!', type: 'success' });
      fetchSequenceDetails(); // Refresh to show updated steps
    } catch (error) {
      console.error('Error deleting step:', error);
      await showAlert({ title: 'Error', message: 'Error deleting step: ' + (error.response?.data?.message || error.message), type: 'error' });
    } finally {
      setDeletingStep(null);
    }
  };

  const handleSyncToInstantly = async () => {
    if (!selectedSequence) {
      await showAlert({ title: 'Warning', message: 'Please select a sequence first', type: 'warning' });
      return;
    }

    const confirmed = await showConfirmation({
      title: 'Sync to Instantly',
      message: 'This will sync all sequence steps to Instantly. Continue?',
      type: 'info'
    });
    
    if (!confirmed) return;

    try {
      setSyncingToInstantly(true);
      
      const response = await apiClient.post(
        `/nurture-sequences/${selectedSequence}/sync-instantly`
      );

      await showAlert({ 
        title: 'Success', 
        message: response.data.message || 'Sequence synced to Instantly successfully!', 
        type: 'success' 
      });
      
      // Refresh sequence details
      fetchSequenceDetails();
    } catch (error) {
      console.error('Error syncing to Instantly:', error);
      await showAlert({ 
        title: 'Error', 
        message: 'Error syncing to Instantly: ' + (error.response?.data?.message || error.message), 
        type: 'error' 
      });
    } finally {
      setSyncingToInstantly(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Sparkles size={32} className="text-purple-600" />
          AI Nurture Dashboard
        </h1>
        <button
          onClick={() => setShowGenerator(!showGenerator)}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
        >
          <Sparkles size={16} />
          {showGenerator ? 'Hide Generator' : 'Generate Campaign'}
        </button>
      </div>

      {/* AI Campaign Generator */}
      {showGenerator && (
        <div className="mb-8">
          <AICampaignGenerator onSequenceGenerated={handleSequenceGenerated} />
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('sequences')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'sequences'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Mail size={16} className="inline mr-2" />
            Sequences
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'events'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Bell size={16} className="inline mr-2" />
            Market Events ({marketEvents.length})
          </button>
          <button
            onClick={() => setActiveTab('attribution')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'attribution'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Map size={16} className="inline mr-2" />
            Attribution
          </button>
        </nav>
      </div>

      {/* Market Events Tab */}
      {activeTab === 'events' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Recent Market Events</h2>
            <button
              onClick={fetchMarketEvents}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
          {marketEvents.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              <Bell size={48} className="mx-auto mb-4 text-gray-400" />
              <p>No market events detected recently.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {marketEvents.map((event) => (
                <div key={event.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{event.company_name}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1).replace('_', ' ')} • 
                        {event.source && ` Source: ${event.source}`}
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Detected: {new Date(event.detected_at).toLocaleString()}
                      </p>
                      {event.relevance_score && (
                        <p className="text-xs text-gray-500 mt-1">
                          Relevance: {event.relevance_score}%
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {!event.processed && (
                        <button
                          onClick={() => handleTriggerSequence(event.id)}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                        >
                          Trigger Sequence
                        </button>
                      )}
                      {event.processed && (
                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-md text-sm">
                          Processed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Attribution Tab */}
      {activeTab === 'attribution' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Attribution Analysis</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                View Attribution for Lead (Enter Lead ID)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter lead ID..."
                  value={selectedLeadForAttribution || ''}
                  onChange={(e) => setSelectedLeadForAttribution(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            {selectedLeadForAttribution && (
              <AttributionVisualizer leadId={selectedLeadForAttribution} />
            )}
            {!selectedLeadForAttribution && (
              <div className="text-center text-gray-500 py-8">
                <Map size={48} className="mx-auto mb-4 text-gray-400" />
                <p>Enter a lead ID to view their attribution path.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sequences List */}
      {activeTab === 'sequences' && (
        <>
      {loading ? (
        <div className="text-center p-8">Loading sequences...</div>
      ) : sequences.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <Sparkles size={48} className="mx-auto mb-4 text-gray-400" />
          <p>No nurture sequences found.</p>
          <p className="text-sm mt-2">Click "Generate Campaign" to create your first AI-powered nurture sequence.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sequences List */}
          <div className="lg:col-span-2 space-y-4">
            {sequences.map((sequence) => (
              <div
                key={sequence.id}
                className={`bg-white rounded-lg shadow p-6 cursor-pointer border-2 ${
                  selectedSequence === sequence.id ? 'border-purple-500' : 'border-gray-200'
                }`}
                onClick={() => setSelectedSequence(sequence.id)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{sequence.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{sequence.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {sequence.active_enrollments || 0} active
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail size={14} />
                        {sequence.total_sends || 0} sent
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingUp size={14} />
                        {parseFloat(sequence.avg_reply_rate || 0).toFixed(1)}% reply rate
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {sequence.status === 'active' ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePause(sequence.id);
                        }}
                        className="px-3 py-1 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 flex items-center gap-1"
                      >
                        <Pause size={14} />
                        Pause
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActivate(sequence.id);
                        }}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center gap-1"
                      >
                        <Play size={14} />
                        Activate
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sequence Details & Predictions */}
          {selectedSequence && sequenceDetails && (
            <div className="space-y-6">
              {/* Enroll Leads Button */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Enroll Leads</h3>
                  <button
                    onClick={handleOpenEnrollModal}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2 text-sm"
                  >
                    <UserPlus size={16} />
                    Enroll Leads
                  </button>
                </div>
                <p className="text-sm text-gray-600">
                  Select leads to enroll in this nurture sequence. They will start receiving emails automatically.
                </p>
              </div>

              {/* Sync to Instantly Button */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Sync to Instantly</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Manually sync this sequence and all its steps to Instantly.ai
                    </p>
                  </div>
                  <button
                    onClick={handleSyncToInstantly}
                    disabled={syncingToInstantly}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                  >
                    {syncingToInstantly ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Send to Instantly
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Sequence Steps */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Sequence Steps</h3>
                <div className="space-y-4">
                  {sequenceDetails.steps?.map((step, index) => (
                    <div key={step.id} className="border-l-4 border-purple-500 pl-4 py-3 bg-gray-50 rounded-r">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium text-gray-900">
                              Step {step.step_order}: {step.step_name}
                            </p>
                            <button
                              onClick={() => handleDeleteStep(step.id)}
                              disabled={deletingStep === step.id || sequenceDetails.status === 'active'}
                              className="ml-2 px-2 py-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                              title={sequenceDetails.status === 'active' ? 'Pause sequence to delete steps' : 'Delete step'}
                            >
                              {deletingStep === step.id ? (
                                <>
                                  <RefreshCw size={14} className="animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 size={14} />
                                  Delete
                                </>
                              )}
                            </button>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            <div className="font-medium mb-1">Subject Line:</div>
                            <div className="bg-white p-2 rounded border border-gray-200 mb-2">
                              {step.subject_line}
                            </div>
                            {step.subject_line?.includes('{{') && (
                              <div className="text-xs bg-blue-50 p-2 rounded border border-blue-200 mt-2">
                                <div className="font-medium text-blue-900 mb-1">📧 Preview (what recipients will see):</div>
                                <div className="text-blue-700 italic">
                                  {step.subject_line
                                    ? step.subject_line.replace(/\{\{first_name\}\}/gi, 'John')
                                        .replace(/\{\{last_name\}\}/gi, 'Smith')
                                        .replace(/\{\{company_name\}\}/gi, 'ABC Hospital')
                                        .replace(/\{\{job_title\}\}/gi, 'CEO')
                                    : step.subject_line}
                                </div>
                                <div className="text-blue-600 mt-1 text-xs">
                                  💡 Tokens like {'{{first_name}}'} are automatically replaced with each lead's actual data
                                </div>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Delay: {step.delay_days} days • Send: {step.optimal_send_time || '09:00'}
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            {step.template_variation_id ? (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                                ✅ Using Template
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                                {step.ai_generate_content ? '🤖 AI Generation' : '⚠️ No Template'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Template Assignment */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Assign Template:
                        </label>
                        <div className="flex gap-2 items-start">
                          <div className="flex-1 relative">
                            <select
                              value={step.template_variation_id ? 'assigned' : ''}
                              onChange={(e) => {
                                if (e.target.value && e.target.value !== 'assigned') {
                                  handleAssignTemplate(step.id, e.target.value);
                                }
                              }}
                              disabled={updatingStep === step.id || loadingTemplates}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 max-h-32 overflow-y-auto"
                              style={{ maxHeight: '200px' }}
                            >
                              <option value="">
                                {step.template_variation_id ? 'Change template...' : 'Select a template...'}
                              </option>
                              {templates.map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.name || 'Unnamed Template'} {template.status !== 'approved' ? `(${template.status})` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          {updatingStep === step.id && (
                            <div className="flex items-center px-3 text-sm text-gray-500 whitespace-nowrap">
                              <RefreshCw size={16} className="animate-spin mr-2" />
                              Updating...
                            </div>
                          )}
                        </div>
                        {step.template_variation_id && (
                          <p className="text-xs text-gray-500 mt-1">
                            Template assigned. AI generation is disabled for this step.
                          </p>
                        )}
                        {!step.template_variation_id && step.ai_generate_content && (
                          <p className="text-xs text-yellow-600 mt-1">
                            ⚠️ Currently using AI generation. Assign a template to use your email templates instead.
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Conversion Predictions */}
              {predictions.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Target size={20} />
                    Conversion Predictions
                  </h3>
                  <div className="space-y-3">
                    {predictions.slice(0, 5).map((prediction) => (
                      <div key={prediction.lead_id} className="border border-gray-200 rounded p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-gray-900">{prediction.lead_name}</p>
                            <p className="text-sm text-gray-500">{prediction.lead_company}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-purple-600">
                              {prediction.conversion_probability}%
                            </p>
                            <p className="text-xs text-gray-500">conversion</p>
                          </div>
                        </div>
                        <div className="flex gap-4 text-xs text-gray-600 mt-2">
                          <span>Reply: {prediction.reply_probability}%</span>
                          <span>Step: {prediction.current_step}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
        </>
      )}

      {/* Enroll Leads Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Enroll Leads in Sequence</h2>
              <button
                onClick={() => {
                  setShowEnrollModal(false);
                  setSelectedLeadIds([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 flex-1 overflow-y-auto">
              {loadingLeads ? (
                <div className="text-center py-8">Loading available leads...</div>
              ) : availableLeads.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users size={48} className="mx-auto mb-4 text-gray-400" />
                  <p>No available leads to enroll.</p>
                  <p className="text-sm mt-2">All ready leads are already enrolled in this sequence.</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">
                        {availableLeads.length} lead(s) available • {selectedLeadIds.length} selected
                      </p>
                    </div>
                    <button
                      onClick={handleSelectAll}
                      className="px-3 py-1 text-sm text-purple-600 hover:text-purple-700 border border-purple-600 rounded-md"
                    >
                      {selectedLeadIds.length === availableLeads.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                              <input
                                type="checkbox"
                                checked={selectedLeadIds.length === availableLeads.length && availableLeads.length > 0}
                                onChange={handleSelectAll}
                                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              />
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {availableLeads.map((lead) => (
                            <tr
                              key={lead.id}
                              className={`hover:bg-gray-50 cursor-pointer ${
                                selectedLeadIds.includes(lead.id) ? 'bg-purple-50' : ''
                              }`}
                              onClick={() => handleToggleLeadSelection(lead.id)}
                            >
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedLeadIds.includes(lead.id)}
                                  onChange={() => handleToggleLeadSelection(lead.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                />
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {lead.first_name || lead.last_name
                                  ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
                                  : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">{lead.email || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{lead.company_name || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{lead.job_title || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEnrollModal(false);
                  setSelectedLeadIds([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={enrolling}
              >
                Cancel
              </button>
              <button
                onClick={handleEnrollLeads}
                disabled={selectedLeadIds.length === 0 || enrolling}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {enrolling ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Enrolling...
                  </>
                ) : (
                  <>
                    <UserPlus size={16} />
                    Enroll {selectedLeadIds.length > 0 ? `${selectedLeadIds.length} ` : ''}Lead(s)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Universal Modals */}
      <ConfirmationModal {...confirmationModal} />
      <AlertModal {...alertModal} />
    </div>
  );
}

